<template>
  <div v-if="displayed" class="indicator-input" :class="{ min, nano }">

    <!-- Input used for display only in a very small space (ongoing or completed field tours) -->
    <div v-if="nano" class="v-centerer indicator-input__nano">
      <input
        v-model="value"
        class="generic-border indicator-input__nano__input"
        :class="{
          'generic-border--gray': valueIsEmpty,
          'generic-border--red': !valueIsEmpty && objectiveOverrunByValue(range),
          'generic-border--green': !valueIsEmpty && !objectiveOverrunByValue(range),
        }"
        :disabled="disabled"
        @keypress="onKeypress($event)"
        @keyup="onKeyup($event)"
        @blur="save(true)"
      >
      <div class="indicator-input__nano__clickoverlay v-centerer">
        <div
          v-if="!isIndicatorCalculated"
          class="indicator-input__nano__clickoverlay__minus"
          @click="decr"
        >
          &minus;
        </div>
        <div
          class="indicator-input__nano__clickoverlay__center v-centerer"
          @click="refreshModalCalcIndicator(indicator.id)"
        >
          <div v-if="valueIsEmpty" class="field-hint">
            {{ indicator.name }}
          </div>
        </div>
        <div
          v-if="!isIndicatorCalculated"
          class="indicator-input__nano__clickoverlay__plus"
          @click="incr"
        >
          &plus;
        </div>
      </div>
    </div>


    <!-- Normal input used in medium spaces. For example in detailed indicator -->
    <div v-else class="content">
      <CloseButton v-if="!min" @click.native="hide()" />
      <div class="indicator-input__head v-centerer">

        <!--Head of the editor (title and date selection) -->
        <h2 class="basic-sub-title indicator-input__head__title">
          <span>{{ headTitle }}</span>
          <span v-if="teamName" class="field-hint ml3em">{{ teamName }}</span>
        </h2>
        <div class="indicator-input__head__date-selector generic-border generic-border--small-text-btn">
          <div
            class="indicator-input__head__date-selector__left generic-border--small-text-btn h-v-centerer"
            @click="$emit('clickPrevDate')"
          >
            <Icon>{{ arrowDownLine }}</Icon>
          </div>
          <div class="indicator-input__head__date-selector__date v-centerer">
            {{ currentDate }}
          </div>
          <div
            class="indicator-input__head__date-selector__right generic-border--small-text-btn h-v-centerer"
            @click="$emit('clickNextDate')"
          >
            <Icon>{{ arrowDownLine }}</Icon>
          </div>
        </div>
      </div>

      <!-- Values edition -->
      <div class="indicator-input__values v-centerer f-w">

        <!-- Current value edition -->
        <div class="indicator-input__values__current v-centerer" :style="inputBgStyle">

          <!-- Edition when the value is a simple number -->
          <div v-if="!isIndicatorCalculated" class="indicator-input__values__current__single v-centerer">
            <div class="indicator-input__values__current__single__wrapper">
              <div class="indicator-input__values__current__single__legend legend">
                {{ $t('message.VALUE') }}
              </div>
              <div class="indicator-input__values__current__single__field v-centerer">
                <div class="indicator-input__values__current__single__decr" @click="decr">
                  &minus;
                </div>
                <div class="indicator-input__values__current__single__field__size-wrapper">
                  <div ref="inputMonitor" class="indicator-input__values__current__single__field__size-monitor">
                    {{ value }}
                  </div>
                  <input
                    ref="input"
                    v-model="value"
                    type="text"
                    :class="inputClasses"
                    @keypress="onKeypress($event)"
                    @keyup="onKeyup($event)"
                    @blur="save(true)"
                  >
                </div>
                <div class="indicator-input__values__current__single__incr" @click="incr">
                  &plus;
                </div>
              </div>
            </div>
            <div class="indicator-input__values__current__single__objective">
              <div class="legend legend--bottom-margin" @click="$refs.objectiveField.doFocus()">
                {{ $t('message.OBJECTIVE_SHORT') }}
              </div>
              <div class="v-centerer indicator-input__values__current__single__objective__value">
                <EditableLabel
                  ref="objectiveField"
                  :value="currentObjective"
                  :class="currentValOverrunObjective"
                  @input="saveObjective"
                />
                <Icon @click.native="$refs.objectiveField.doFocus()">
                  {{ editMedium }}
                </Icon>
              </div>
            </div>
            <div class="indicator-input__values__current__single__delete h-v-centerer" @click="promptDeleteVal()">
              <Icon>{{ deleteMedium }}</Icon>
            </div>
          </div>
          <!-- Edition when the value is a calculated indicator -->
          <div v-else class="v-centerer indicator-input__values__current__calculated">
            <div
              v-for="(calcKey, index) in indicator.calcItems"
              :key="index"
              v-quick-title="calcKey.calculationKey === 'indicator-value'
                ? expressionItemDisplayedString(calcKey, null, true)
                : null
              "
              class="indicator-input__values__current__calculated__item"
              :class="expressionItemClass(calcKey)"
            >
              <input
                v-if="CALC_FIELD_KEYS.some(ck => ck.calculationKey === calcKey.calculationKey)"
                v-model="calcValues[`${calcKey.id}`]"
                :placeholder="expressionItemDisplayedString(calcKey)"
                :readonly="calcKey.calculationKey !== 'value'"
                class="indicator-input__values__current__calculated__item__field"
                type="text"
                @keypress="onKeypress($event)"
                @keyup="onKeyup($event)"
                @blur="save(true)"
                @click="onCalcIndicatorClick(calcKey)"
              >
              <div v-else>
                {{ expressionItemDisplayedString(calcKey) }}
              </div>
            </div>
            <div
              class="
                indicator-input__values__current__calculated__item
                indicator-input__values__current__calculated__item--result
              "
            >
              {{ `= ${displayedValue}` }}
            </div>
          </div>
        </div>
      </div>

      <div class="indicator-input__comment">
        <div class="legend indicator-input__comment__legend">
          {{ $t('message.COMMENTS') }}
        </div>
        <EditableLabel
          ref="commentLabel"
          v-model="comment"
          v-quick-title="valueCommentTitle"
          :disabled="valueIsEmpty"
          :hint="$t('message.COMMENTS')"
          :placeholder="$t('message.COMMENTS')"
          use-tiptap-editor
          display-tiptap-menu
          display-tiptap-tasks
          hide-undo-redo
          @input="commentEdited"
        />
      </div>
    </div>

    <Modal :value="modalCalcIndicator !== null" class="indicator-input__modal-wrapper" @input="closeModalCalc">
      <IndicatorInput
        v-if="modalCalcIndicator !== null"
        :indicator="modalCalcIndicator"
        :focused-date="focusedDate"
        :refresh-range="refreshRange"
        class="indicator-input__modal-indicator"
        min
        shown
        @commentEdit="$emit('commentEdit')"
        @clickPrevDate="$emit('clickPrevDate')"
        @clickNextDate="$emit('clickNextDate')"
      />
    </Modal>
  </div>
</template>

<script>
/**
 * Indicator item displaying a chart
 *
 * @vue-prop {Object} indicator - The indicator object from the DB
 * @vue-prop {Object} focusedDate - Moment instance. Date of the value that should be focused
 * @vue-prop {Object} refreshRange - Range (start: Moment, end: Moment) of values to request
 *                                   when calling XHR for a value update
 * @vue-prop {Boolean} disabled - Disable this component
 * @vue-prop {Boolean} min - True if displaying the minimalistic interface of this component
 * @vue-prop {Boolean} nano - True if displaying the even more minimalistic interface (field and +/-)
 * @vue-prop {Boolean} shown - Changing this value calls sho / hide methods
 * @vue-prop {Boolean} focusOnMount - If true, input field will receive focus in mounted
 * @vue-prop {Boolean} hideThemeName - If true, prevent showing the name of the theme in th header
 * @vue-prop {Boolean} minimalDate - If true, only show the minimal date format
 *
 * @vue-event {} inputDone - Emitted when hiding the component
 * @vue-event {Object} dateSet - Emitted when user changed the date
 *
 */

import axios from 'axios';
import { isEqual } from 'lodash';
import toastr from 'toastr';
// Vuex
import {
  SET_INDICATOR_VALUE,
  DELETE_INDICATOR_VALUE,
  SET_INDICATOR_OBJECTIVE,
  FETCH_INDICATOR,
} from '@/store/action-types';
// Components
import CloseButton from '@/components/close-button.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import mixinCommonColors from '@/mixins/common-colors.vue';
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import SocketMessages from '@/utils/classes/SocketMessages';
import {
  COMMENT_TYPE,
  calert,
  getLastWorkingDay,
  stringToCharCodes,
  decodeHTML,
} from '@/utils/main';
import {
  valueKeyFormat,
  expressionItemDisplayedString,
  expressionItemClass,
  CALC_FIELD_KEYS,
} from '@/utils/indicator-utils';
import { ShizenGlobalEvent, globalEventDispatcher } from '@/utils/event-dispatcher/global';
import { arrowDownLine, deleteMedium, editMedium } from '@/assets/icons';

export default {

  name: 'IndicatorInput',

  components: {
    CloseButton,
    EditableLabel,
    Icon,
    Modal,
  },

  mixins: [mixinCommonColors, mixinSocketHandler],

  props: {
    indicator: {
      type: Object,
      required: true,
    },
    focusedDate: {
      type: Object,
      default: null,
    },
    refreshRange: {
      type: Object,
      default: null,
    },
    disabled: Boolean,
    min: Boolean,
    nano: Boolean,
    shown: Boolean,
    focusOnMount: Boolean,
    hideThemeName: Boolean,
    minimalDate: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      deleteMedium,
      editMedium,
      CALC_FIELD_KEYS,
      // Constant: list of allowed char in the input
      allowedCharCodes: stringToCharCodes('-+.,0123456789eE'),
      selectedDate: null,
      displayed: false,
      // Final value of the day (can be manually entered or calculated if calcItems is not empty)
      value: '',
      // If this indicator uses calc items, this is the array of values that can be entered
      calcValues: {},
      values: [],
      comment: '',
      timeoutIds: {},
      // If set true, next message will be displayed
      displayNextMsg: false,
      // Abort controllers for axios requests
      saveRequestController: null,
      editCommentAbortController: null,
      // Allow editing indicators used in calculation
      modalCalcIndicator: null,
    };
  },

  computed: {
    /** String: Overlay title */
    headTitle() {
      if (this.indicator.name) {
        return this.hideThemeName
          ? decodeHTML(this.indicator.name)
          : decodeHTML(`${this.indicator.themeName} - ${this.indicator.name}`);
      }
      return this.$t('message.LOADING');
    },
    /** String: When editing an indicator of another team (clicking calculated value of another team) */
    teamName() {
      if (this.currentTeam.id !== Number(this.indicator.team) && this.indicator.teamName) {
        // When editing an indicator of another team (clicking calculated value of another team)
        return `${this.$t('message.CONTENT_BETWEEN_PARENTHESES', { content: this.indicator.teamName })}`;
      }
      return null;
    },
    /** String: displayed date above the value field */
    currentDate() {
      // Value of the selected day, display the day's date
      if (this.isDayValue) {
        const format = this.minimalDate ? this.$t('minDateFormat') : `ddd ${this.$t('minDateFormat')}`;
        return this.range.start.format(format);
      }
      // Value of a week or a month, display the range
      const df = this.$t('minDateFormat');
      return `${this.range.start.format(df)} - ${this.range.end.format(df)}`;
    },
    /** String: Index of the current value in the values and objectives objects */
    currentIndex() {
      return this.selectedDate ? this.effectiveValueDate.format(valueKeyFormat) : '';
    },
    /** Object: Value of the focused date */
    currentValueObject() {
      return this.inputValues[this.currentIndex];
    },
    /** Boolean: True if the values are calculated with IndicatorCalcItem */
    isIndicatorCalculated() {
      return this.indicator.calcItems && this.indicator.calcItems.length > 0;
    },
    valueIsEmpty() {
      return !this.isIndicatorCalculated
        ? this.value === null || this.value === ''
        : !Object.keys(this.calcValues).some(key => ![null, ''].includes(this.calcValues[key]));
    },
    valueAsNumber() {
      return this.value ? Number(this.value.replace(',', '.')) : 0;
    },
    valueIsInvalid() {
      return isNaN(this.valueAsNumber);
    },
    inputClasses() {
      return { invalid: !this.valueIsEmpty && this.valueIsInvalid };
    },
    /** If no value is set, display a hint so that the user understands that he cannot add a comment */
    valueCommentTitle() {
      return this.valueIsEmpty ? this.$t('message.INDIC_VALUE_BEFORE_COMMENT') : '';
    },
    /** Company color settings */
    inputBgStyle() {
      if (this.min) {
        return {};
      }
      return {
        background: this.currentValOverrunObjective.overrun
          ? this.indicatorInputEditOverrun
          : this.companyChartColor,
      };
    },
    /** Objective at current date */
    currentObjective() {
      return this.objectiveInRange();
    },
    /** Is the current value overrunning the objective */
    currentValOverrunObjective() {
      return { overrun: this.objectiveOverrunByValue(this.range) };
    },
    /** Currnt range of the value to edit (can be a day, a week or a month) */
    range() {
      return this.getRangeFromDate();
    },
    /** Mid-point of range */
    effectiveValueDate() {
      const date = this.range.start.clone();
      // DEBUG: Monitoring this change (can be removed if version > 1.7.0)
      // while (!this.dateIsAWorkingDays(date)) {
      //   date.add(1, 'd');
      // }
      return date;
    },
    /** String: Value displayed when editing a calculated indicator */
    displayedValue() {
      if (this.currentValueObject && this.currentValueObject.value !== null) {
        return String(Number(this.currentValueObject.value).sToFixed(2));
      }
      return '';
    },
    /** Values set in Vuex */
    inputValues() {
      const indic = this.$store.state.indicators[`${this.indicator.id}`];
      return (indic && indic.inputValues) || {};
    },
    /** Objectives set in Vuex */
    objectives() {
      const indic = this.$store.state.indicators[`${this.indicator.id}`];
      return (indic && indic.objectives) || {};
    },
    /** Boolean: Is the input period one day */
    isDayValue() {
      return this.indicator.inputPeriodUnit === 'd';
    },
    /** Boolean: Static or incremental values can be interrupted by setting a null value */
    isStaticInterruption() {
      // TODO: Allow removing a static values interruption
      return [undefined, null].includes(this.valueInRange().value);
    },
  },

  watch: {
    focusedDate(val) {
      this.selectedDate = this.$moment(val);
    },
    selectedDate() {
      this.comment = '';
      this.setValue(true);
      this.$nextTick(() => {
        if (this.modalCalcIndicator !== null && this.modalCalcIndicator.id) {
          this.refreshModalCalcIndicator(this.modalCalcIndicator.id);
        }
      });
    },
    inputValues() {
      this.setValue(true);
    },
    indicator(newVal, oldVal) {
      if (newVal.id !== oldVal.id && this.currentValueObject) {
        this.setValue();
      }
    },
    shown(newVal) {
      if (newVal) {
        this.show();
      } else {
        this.hide();
      }
    },
    currentValOverrunObjective(newVal) {
      this.$emit('overrun', !!newVal.overrun);
    },
    value() {
      // Adapt the size of the input
      this.$nextTick(() => {
        if (this.$refs.input && this.$refs.inputMonitor) {
          this.$refs.input.style.width = `${this.$refs.inputMonitor.offsetWidth}px`;
        }
      });
    },
  },

  mounted() {
    // Handle props setup options
    if (this.shown) {
      this.show();
      if (this.focusOnMount) {
        this.$nextTick(this.focusInputField);
      }
    }

    this.$nextTick(() => {
      if (this.$refs.input && this.$refs.inputMonitor) {
        this.$refs.input.style.width = `${this.$refs.inputMonitor.offsetWidth}px`;
      }
    });
  },

  methods: {
    expressionItemClass,
    expressionItemDisplayedString,
    /**
     * Does the value overrun the objective when summing up values within a range
     * @param  {Object} range - object with start and end. If not spsecified, uses current range
     * @return {Boolean} - whether the objective is overrun
     */
    objectiveOverrunByValue(range) {
      const oIsMax = Number(this.indicator.objectiveIsMax);
      const obj = this.objectiveInRange(range);
      const val = this.valueInRange(range).value;
      return (val < obj && oIsMax === 0) || (val > obj && oIsMax === 1);
    },
    /** Get the objective at given date index */
    objectiveAtIndex(index) {
      const { objective } = this.indicator;
      if (this.objectives) {
        const obj = this.objectives[index];
        return isNaN(obj) ? objective : obj;
      }
      return objective;
    },
    /**
     * Get the value at specified range
     * @param  {Object} range - object with start and end. If not spsecified, uses current range
     * @return {Object} - A classic IndicatorValue object where value and calcValues have been cumulated
     */
    valueInRange(range) {
      const valueObj = { value: null, calcValues: {} };
      // If the input period is a week or a month, the value is the sum of all values within the period
      this.loopThroughRange((iDate) => {
        const valObj = this.inputValues[iDate.format(valueKeyFormat)];
        if (valObj) {
          const { value, calcValues } = valObj;
          if (value !== null && !isNaN(value)) {
            valueObj.value = (valueObj.value || 0) + Number(value);
          }
          Object.keys(calcValues).forEach((key) => {
            if (calcValues[key].value && !isNaN(calcValues[key].value)) {
              valueObj.calcValues[key] = (valueObj.calcValues[key] || 0) + Number(calcValues[key].value);
            }
          });
        }
      }, range);
      return valueObj;
    },
    /**
     * Get the objective at specified range
     * @param  {Object} range - object with start and end. If not spsecified, uses current range
     * @return {Number} - objective as a numeric value
     */
    objectiveInRange(range) {
      let obj = 0;
      // If the input period is a week or a month, sum the objectives within the period
      this.loopThroughRange((iDate) => {
        obj += Number(this.objectiveAtIndex(iDate.format(valueKeyFormat)));
      }, range);
      return obj;
    },
    /**
     * Update editable value from the selected date
     * @param  {Boolean} [updateComment] - allow or not to replace the current comment by
     * the value in the Vuex store
     */
    setValue(updateComment) {
      const { value, calcValues } = this.valueInRange();
      this.value = value === null || isNaN(value) ? '' : String(value);
      this.calcValues = Object.assign({}, calcValues);

      // Setup comment
      if (!this.currentValueObject) {
        return;
      }
      const { commentText } = this.currentValueObject;
      if (updateComment || !this.comment) {
        this.comment = commentText || '';
      }
    },
    /** Helper to loop through the days of the current range */
    loopThroughRange(doWhat, range) {
      const { start, end } = range || this.range;
      const iDate = end.clone();
      while (!iDate.isBefore(start)) {
        doWhat(iDate);
        iDate.subtract(1, 'd');
      }
    },
    /** Helper to find the beginning of a period according to the indicator settings */
    getRangeFromDate(selectedDate) {
      selectedDate = selectedDate || this.selectedDate || this.$moment();
      if (this.indicator.inputPeriodUnit === 'w') {
        return {
          start: selectedDate.clone().startOf('isoWeek'),
          end: selectedDate.clone().endOf('isoWeek'),
        };
      }
      if (this.indicator.inputPeriodUnit === 'M') {
        return {
          start: selectedDate.clone().startOf('month'),
          end: selectedDate.clone().endOf('month'),
        };
      }
      return {
        start: selectedDate.clone().startOf('d'),
        end: selectedDate.clone().endOf('d'),
      };
    },
    /** Refresh the value of the current day */
    refreshCurrentValue() {
      const params = {
        indicatorId: this.indicator.id,
        range: {
          start: this.range.start.format('YYYY-MM-DD'),
          end: this.range.end.set({ h: 23, m: 59, s: 59 }).format('YYYY-MM-DD HH:mm:ss'),
        },
      };
      this.$store.dispatch(FETCH_INDICATOR, params);
    },
    /**
     * Handle clickon calculated indicator fields. If the field is an indicator value,
     * Allow editing it through a modal.
     * @param  {Object} calcKey Data related to the field that was clicked
     */
    onCalcIndicatorClick(calcKey) {
      if (calcKey.calculationKey === 'indicator-value') {
        this.refreshModalCalcIndicator(calcKey.calculatedIndicator);
      }
    },
    /** Refresh the values of the indicator displayed in the modal */
    refreshModalCalcIndicator(indicatorId) {
      const params = {
        indicatorId,
        range: {
          start: this.range.start.format('YYYY-MM-DD'),
          end: this.range.end.set({ h: 23, m: 59, s: 59 }).format('YYYY-MM-DD HH:mm:ss'),
        },
      };
      this.$store.dispatch(FETCH_INDICATOR, params).then(() => {
        if (this.$store.state.indicators[`${indicatorId}`]) {
          this.modalCalcIndicator = this.$store.state.indicators[`${indicatorId}`];
        }
      });
    },
    /** Close the modal show the calculated indicator */
    closeModalCalc() {
      this.modalCalcIndicator = null;
      // Set a .5s timeout to handle user editing the value and closing the modal right away
      // => Refreshing the value right away fetches the previous one
      setTimeout(this.refreshCurrentValue, 500);
    },
    onKeypress(e) {
      // Prevent unallowed keypress input (doesn't prevent invalid value through)
      if (e.charCode > 0 && this.allowedCharCodes.indexOf(e.charCode) === -1) {
        e.preventDefault();
      }
    },
    onKeyup(e) {
      if (e.keyCode === 13) {
        // Pressed Enter
        this.displayNextMsg = true;
        e.target.blur();
      }
    },
    show() {
      this.displayed = true;
      this.selectedDate = this.focusedDate ? this.$moment(this.focusedDate) : null;
      if (this.selectedDate == null) {
        this.selectedDate = getLastWorkingDay(this.App.user.workingDays);
      }
      if (!this.min) {
        this.focusInputField();
      }
    },
    hide() {
      this.displayed = false;
      this.$emit('inputDone');
    },
    changeDate(value) {
      this.selectedDate = value.range.start;
      this.$nextTick(this.focusInputField);
      this.$emit('dateSet', this.selectedDate);
    },
    focusInputField() {
      const elt = this.$refs.input;
      if (typeof elt === 'undefined' || elt.clientWidth === 0) {
        setTimeout(this.focusInputField, 0);
      } else {
        elt.focus();
      }
    },
    incr() {
      if (this.valueIsInvalid) return;
      // Simple incrementation but that deals with floating-point precision
      this.value = this.valueIsEmpty
        ? '0'
        : String((Math.round(this.valueAsNumber * 10e6) + 10e6) / 10e6);
      this.save(true);
    },
    decr() {
      if (this.valueIsInvalid) return;
      // Simple decrementation but that deals with floating-point precision
      this.value = this.valueIsEmpty
        ? '0'
        : String((Math.round(this.valueAsNumber * 10e6) - 10e6) / 10e6);
      this.save(true);
    },
    /** Triggered on editable-label input event for the comment field */
    commentEdited() {
      if (!this.checkEditable()) {
        return;
      }
      const value = this.currentValueObject;
      if (value.commentId === 0 && this.comment === '') {
        // No comment added and no comment saved on DB
        return;
      }
      if (!value.id) {
        // Comment must be attached to an IndicatorValue. If no value, save it or display an error
        if (!isNaN(this.valueAsNumber)) {
          const comment = String(this.comment);
          this.save(true, () => { this.saveComment(comment); }, true);
        } else {
          calert(this.$t('message.INDIC_COMMENT_NEED_VALUE'), [{ title: this.$t('message.CANCEL') }]);
        }
        return;
      }
      this.saveComment();
    },
    /** Save the comment to the server */
    saveComment(comment) {
      comment = comment || this.comment;
      const value = this.currentValueObject;
      // Check if there is an ongoing XHR by checking if editCommentAbortController exists
      if (this.editCommentAbortController) {
        // Abort the ongoing XHR
        this.editCommentAbortController.abort();
      }
      this.editCommentAbortController = new AbortController();
      const params = {
        id: value.commentId,
        itemType: COMMENT_TYPE.indicator,
        itemId: value.id,
        text: comment,
      };
      const config = {
        controller: this.editCommentAbortController,
        signal: this.editCommentAbortController.signal,
      };
      this.$http.post('/server/comments/set.php', params, config).then((response) => {
        const { data } = response;
        toastr.remove();
        if (data instanceof Array) {
          value.commentId = data[0].id;
          value.commentText = data[0].text;
          this.comment = value.commentText;
          this.$emit('commentEdit', data[0]);
          this.sendIndicSocketUpdate();
          toastr.success(this.$t('message.COMMENT_SAVED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      }).catch((err) => {
        if (axios.isCancel(err)) {
          // The XHR has been canceled because a new one started
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /**
     * Save the objective to the server for this day
     * @param  {String|Number} objectiveValue - objective that will be converted to a valid number
     */
    saveObjective(objective) {
      if (!this.checkEditable() || isNaN(objective)) {
        return;
      }
      const params = {
        objective,
        date: this.effectiveValueDate.format('YYYY-MM-DD'),
        range: {
          start: this.range.start.format('YYYY-MM-DD HH:mm:ss'),
          end: this.range.end.format('YYYY-MM-DD HH:mm:ss'),
        },
        indicatorId: this.indicator.id,
      };
      this.$store.dispatch(SET_INDICATOR_OBJECTIVE, params).then(() => {
        toastr.success(this.$t('message.INDICATOR_OBJECTIVE_SAVED'));
        this.sendIndicSocketUpdate();
      });
    },
    /** Check whether this indicator is editable by the current team and display a message if it isn't */
    checkEditable() {
      if (this.indicator.copyPublic > 0) {
        calert(this.$t('message.CANT_EDIT_PUBLIC_INDIC'), [{
          title: this.$t('message.CANCEL'),
        }]);
        return false;
      }
      return true;
    },
    /**
     * Save the current indicator value
     * @param  {Boolean} [noMsgs=false] - if set and true, prevent showing toast message
     * @param  {Function} onSuccess - function called when the save is successfull
     * @param  {Boolean} [force=false] - if set and true, don't check if the new value
     *                                   is different from the previous one
     */
    save(noMsgs, onSuccess, force = false) {
      if (!this.checkEditable() || this.valueIsEmpty) {
        // Don't save ; It will restore the previous number
        return;
      }
      if (!force && isEqual(this.valueInRange(), { value: this.valueAsNumber, calcValues: this.calcValues })) {
        // New value not different from the previous value
        return;
      }
      if (this.valueIsInvalid) {
        if (!noMsgs) {
          toastr.error(this.$t('message.ENTER_A_NUMBER'));
        }
        return;
      }
      const index = this.currentIndex;
      // Debouncing mecanism: Wait some time before sending request. If new request is made, delete previous request.
      // Prevent request spam. Manually/unsilent save aren't delayed and are send immediatly
      if (this.timeoutIds[index]) {
        clearTimeout(this.timeoutIds[index]);
        delete this.timeoutIds[index];
      }
      // Check if save function is already used
      if (this.saveRequestController) {
        this.saveRequestController.abort();
      }
      this.saveRequestController = new AbortController();
      // Assign values to local variables since the request is sent in a timeout
      const date = this.effectiveValueDate.format('YYYY-MM-DD');
      const value = this.valueAsNumber;
      const calcValues = Object.assign({}, this.calcValues);
      // Range of values to refresh
      const { range, refreshRange } = this;
      const rangeParam = { start: range.start.clone(), end: range.end.clone() };
      if (refreshRange) {
        rangeParam.start = rangeParam.start.isBefore(refreshRange.start) ? rangeParam.start : refreshRange.start;
        rangeParam.end = rangeParam.end.isAfter(refreshRange.end) ? rangeParam.end : refreshRange.end;
      }
      // Range of values to delete
      const deletionRange = this.isDayValue
        ? null
        : {
          start: range.start.format('YYYY-MM-DD HH:mm:ss'),
          end: range.end.format('YYYY-MM-DD HH:mm:ss'),
        };
      const indicatorId = this.indicator.id;
      this.timeoutIds[index] = setTimeout(() => {
        this.$store.dispatch(SET_INDICATOR_VALUE, {
          value,
          indicatorId,
          date,
          range: {
            start: rangeParam.start.format('YYYY-MM-DD HH:mm:ss'),
            end: rangeParam.end.format('YYYY-MM-DD HH:mm:ss'),
          },
          deletionRange,
          calcValues,
          controller: this.saveRequestController,
          signal: this.saveRequestController.signal,
        }).then(() => {
          globalEventDispatcher.dispatch(ShizenGlobalEvent.INDICATOR_INPUT_EDITED);
          if (!this.min && (!noMsgs || this.displayNextMsg)) {
            toastr.success(this.$t('message.INDICATOR_VALUE_SAVED'));
            this.displayNextMsg = false;
          }
          this.sendIndicSocketUpdate();
          if (onSuccess) onSuccess();
        });
        delete this.timeoutIds[index];
      }, noMsgs ? 200 : 0);
    },
    /** Confirmation modal to delete the value */
    promptDeleteVal() {
      if (this.currentValueObject && this.currentValueObject.commentId > 0) {
        calert(this.$t('message.CONFIRM_DELETE_INDIC_VAL'), [
          {
            title: this.$t('message.YES'),
            action: this.deleteVal,
            sClass: 'red',
          },
          {
            title: this.$t('message.NO'),
          },
        ]);
      } else {
        this.deleteVal();
      }
    },
    /** Send the delete XHR. If range is > 1 day, delete values for the entire range */
    deleteVal() {
      if (!this.checkEditable()) {
        return;
      }
      if (this.value !== null) {
        const index = this.currentIndex;
        if (this.timeoutIds[index]) {
          clearTimeout(this.timeoutIds[index]);
          delete this.timeoutIds[index];
        }
        const fDate = this.effectiveValueDate.format('YYYY-MM-DD');
        // Range of values to refresh
        const { range, refreshRange } = this;
        const rangeParam = { start: range.start.clone(), end: range.end.clone() };
        if (refreshRange) {
          rangeParam.start = rangeParam.start.isBefore(refreshRange.start) ? rangeParam.start : refreshRange.start;
          rangeParam.end = rangeParam.end.isAfter(refreshRange.end) ? rangeParam.end : refreshRange.end;
        }
        // Range of values to delete
        const deletionRange = this.isDayValue
          ? null
          : {
            start: range.start.format('YYYY-MM-DD HH:mm:ss'),
            end: range.end.format('YYYY-MM-DD HH:mm:ss'),
          };
        this.$store.dispatch(DELETE_INDICATOR_VALUE, {
          indicatorId: this.indicator.id,
          date: fDate,
          range: {
            start: rangeParam.start.format('YYYY-MM-DD HH:mm:ss'),
            end: rangeParam.end.format('YYYY-MM-DD HH:mm:ss'),
          },
          deletionRange,
        }).then(() => {
          this.comment = '';
          toastr.success(this.$t('message.INDICATOR_VALUE_DELETED'));
          this.sendIndicSocketUpdate();
        });
      }
    },
    /** Check if a date is a working day (returns true) */
    dateIsAWorkingDays(date) {
      const wd = this.App.user.workingDays || [true, true, true, true, true, true, true];
      return wd[date.isoWeekday() - 1];
    },
    /** Send socket messages regarding the content of this indicator */
    sendIndicSocketUpdate() {
      const { id, isPublic } = this.indicator;
      this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.INDICATORS, id });
      if (Number(isPublic) === 1) {
        this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.PUBLIC_INDICATOR, id, socketCompany: 1 });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.indicator-input {
  $indicator-input-padding: 0.7rem;
  padding: $indicator-input-padding;
  box-sizing: border-box;

  &:not(.min) {
    z-index: 10002;
  }

  & &__head {
    justify-content: space-between;
    flex-flow: column;
    margin-bottom: .5rem;
    align-items: flex-start;

    &__title {
      flex: 1;
      min-width: 0px;
      font-size: 1.15rem;
      margin-bottom: .5rem;
      margin-top: .3rem;
    }

    &__date-selector {
      flex: 0 0 auto;
      pointer-events: none;

      &.generic-border--small-text-btn {
        padding: 0px;

        &:hover {
          background: $color-gray-background;
        }

        &:active {
          box-shadow: none;
        }
      }

      &__left.generic-border--small-text-btn, &__right.generic-border--small-text-btn {
        pointer-events: all;
        padding: 0px;
        width: 2.14rem;
        height: 2.14rem;

        .icon {
          margin: auto;
          width: 0.8rem;
          height: 0.8rem;
        }
      }

      &__left {
        transform: rotate(90deg);
      }

      &__right {
        transform: rotate(-90deg);
      }

      &__date.generic-border--small-text-btn {
        padding: 0 0.5rem;
      }
    }
  }

  & &__values {

    &__current {
      max-width: 100%;

      &__single {
        max-width: 100%;
        justify-content: space-between;

        &.v-centerer {
          align-items: flex-start;
          flex-wrap: wrap;
        }

        &__wrapper, &__objective, &__delete {
          margin-top: .5rem;
        }

        &__wrapper {
          min-width: 5.5rem;
        }

        &__legend {
          margin-bottom: .7rem;
        }

        &__field {
          border: 1px solid $color-gray-border;
          box-sizing: border-box;
          border-radius: $high-border-radius;
          max-width: 100%;
          transition: box-shadow .2s;

          &:focus-within {
            box-shadow: $color-shizen-blue-btn-shadow;
          }

          input {
            border: none;
            outline: none;
            padding: .7rem 0;
            min-width: 2.5rem;
            max-width: 100%;
            text-align: center;
          }

          &__size-wrapper {
            max-width: calc(100% - 4.6rem);
          }

          &__size-monitor {
            width: fit-content;
            visibility: hidden;
            height: 0px;
          }
        }

        &__incr, &__decr {
          font-weight: 600;
          width: 2rem;
          height: 2.2rem;
          border-radius: $high-border-radius;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          cursor: pointer;

          &:hover {
            background: $color-gray-background-hover;
          }
        }

        &__incr {
          margin-right: .3rem;
        }

        &__decr {
          margin-left: .3rem;
        }

        &__objective {
          margin-left: 1rem;
          text-align: center;
          cursor: pointer;

          &__value {
            padding: 0 0.5rem;

            .editable-label {
              padding: 0.5rem;
            }

            .icon {
              width: 1rem;
              height: 1rem;
              margin-left: 0.2rem;
            }
          }
        }

        &__delete {
          margin-left: .5rem;
          cursor: pointer;
          text-align: center;
          flex: 1;
          align-self: flex-end;

          .icon {
            width: 1.3rem;
            height: 1.3rem;
            padding: .6rem;
            border-radius: 100px;
            margin-bottom: .1rem;
            overflow: visible;
            transition: background .2s;
          }

          &:hover {
            .icon {
              background: $color-hoverbtn-red;
            }
          }
        }
      }

      &__calculated {
        &__item {
          margin: 0 1px;

          input.indicator-input__values__current__calculated__item__field {
            border: 1px solid $color-gray-border;
            border-radius: $high-border-radius;
            padding: .7rem 0;
            width: 2.5rem;
            text-align: center;
          }

          &--result {
            white-space: nowrap;
            margin-right: .5rem;
          }
        }
      }
    }
  }

  & &__comment {
    margin-top: 1rem;

    &__legend {
      margin-bottom: 0.7rem;
    }
  }

  &.nano {
    display: flex;
    align-items: center;
    background: transparent;

    input {
      // width: 3.5em;
      // box-sizing: border-box;
      // padding: 0.2em 0.4em;

      &.overrun {
        background: #ffc8c8;
      }
    }
  }

  &__nano {
    position: relative;

    &__clickoverlay {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: 0 0.5rem;

      &__center {
        flex: 1;
        height: 100%;
        min-width: 0px;

        .field-hint {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          margin: 0 .2rem;
          text-align: center;
        }
      }

      &__plus, &__minus {
        flex: 0 0 auto;
        height: calc(100% - .4rem);
        margin: .2rem;
        padding: 0 .6rem;
        display: flex;
        align-items: center;
        font-weight: 600;
        background: #fff;
        border-radius: $high-border-radius;
      }
    }
  }


  &__modal-wrapper {
    .dark-overlay-content {
      max-width: 450px;
      max-height: 500px;

      .content-wrapper {
        background: $color-body-background;
        padding: 3.2rem 1rem 1rem 1rem;
        box-sizing: border-box;
      }
    }
  }

  &__modal-indicator.indicator-input.min {
    box-sizing: border-box;
    background: #fff;
    border-radius: $high-border-radius;

    .indicator-input__values__current__single__field input {
      padding: .7rem 0;
      width: 2.5rem;
    }
  }
}

@media (max-width: $smartphone-landscape) {
}
</style>
